// Copyright (c) 2026 Digital Asset (Switzerland) GmbH and/or its affiliates. All rights reserved.
// SPDX-License-Identifier: Apache-2.0
import * as sdk from '@canton-network/dapp-sdk';
import React, { useEffect, useState } from 'react';
import Loading from '../components/Loading';
import { useConfig } from '../hooks/other/useConfig';
import { ACSContainer, ACSHelper } from '../utils/ACS/index';
import LedgerApiClientV2 from '../utils/LedgerApiClientV2';
import { useJsonApiClient, useWsSupport } from './JsonApiContext';
import { LedgerApiContextV2 } from './LedgerApiContext';
import { useLocalStorage } from './LocalStorageContext';

export const LedgerApiClientProvider = ({ children }: React.PropsWithChildren) => {
  const config = useConfig();
  const jsonApi = useJsonApiClient();
  // Websockets supported Ledger API v2 / ACS
  // do not pass wsSupport  to LedgerApiClientV2 in order to disable websockets
  const wsSupport = useWsSupport();

  const { localStorageState } = useLocalStorage();
  const { activeContractsLogsEnabled } = localStorageState;

  const [ledgerApiClientV2, setLedgerApiClientV2] = useState<LedgerApiClientV2 | undefined>(
    undefined,
  );
  useEffect(() => {
    // This should be passed as part of the LedgerApiClientV2 or ACS helper / container configs.
    // Revisit when extracting LedgerApiClient to common package.
    ACSHelper.cacheLifespanMs = config.acs.cacheLifespanMs;
    ACSContainer.useLogs = !!activeContractsLogsEnabled;
    // client for JSON API v2
    const apiClientV2 = new LedgerApiClientV2(
      config.app.enableDappLogin ? sdk.ledgerApi : jsonApi,
      config.app.deduplication_duration_in_seconds,
      wsSupport,
      config.acs.database,
    );
    setLedgerApiClientV2(apiClientV2);
  }, [jsonApi, wsSupport, config, activeContractsLogsEnabled]);

  if (!ledgerApiClientV2) {
    return <Loading text="Initializing ledger client" />;
  }

  return (
    <LedgerApiContextV2.Provider value={ledgerApiClientV2}>{children}</LedgerApiContextV2.Provider>
  );
};
